import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_MODEL, getModelById } from '../config/models';
import { getToolById } from '../config/tools';

export const useChatStore = create(
  persist(
    (set, get) => ({
      // State
      messages: [],
      currentConversationId: null,
      selectedTool: null,
      selectedSubType: null, // 선택된 도구의 서브타입 (engine 값)
      selectedModel: DEFAULT_MODEL,
      previousModel: null, // 도구 해제 시 복원용
      isProcessing: false,
      isLoadingConversation: false, // 대화 불러오는 중
      streamingMessage: null,
      newChatRequested: false, // Flag to signal new chat was requested

      // 팟캐스트 퍼널 상태
      podcastFunnel: {
        isActive: false,
        step: 1, // 1: 주제, 2: 스타일, 3: 길이, 4: 확인
        topic: '',
        style: null, // 'news' | 'expert' | 'casual'
        duration: null, // 3 | 5 | 10
      },

      // Actions
      setMessages: (messages) => set({ messages }),

      addMessage: (message) => set((state) => ({
        messages: [...state.messages, message]
      })),

      updateMessage: (messageId, updates) => set((state) => ({
        messages: state.messages.map((msg) =>
          msg.id === messageId ? { ...msg, ...updates } : msg
        )
      })),

      updateStreamingMessage: (content) => set({ streamingMessage: content }),

      setCurrentConversation: (id) => set({ currentConversationId: id }),

      setSelectedTool: (toolId) => set((state) => {
        const newToolId = state.selectedTool === toolId ? null : toolId;
        const tool = newToolId ? getToolById(newToolId) : null;

        // 🔍 DEBUG: 도구 선택 로그
        console.log(
          `%c[STORE] 도구 선택: ${state.selectedTool || '없음'} → ${newToolId || '해제'}`,
          'color: #a855f7; font-weight: bold;'
        );

        // 도구가 바뀌면 서브타입 초기화 (첫 번째 서브타입으로 자동 선택)
        let defaultSubType = null;
        if (tool?.subTypes && tool.subTypes.length > 0) {
          defaultSubType = tool.subTypes[0];
          console.log(
            `%c[STORE] 기본 서브타입 선택: ${defaultSubType.name} (engine: ${defaultSubType.engine})`,
            'color: #06b6d4; font-weight: bold;'
          );
        }

        // 도구에 연결된 모델이 있으면 자동 변경
        if (tool?.linkedModelId) {
          const linkedModel = getModelById(tool.linkedModelId);
          console.log(
            `%c[STORE] 연결 모델 자동 선택: ${linkedModel?.name}`,
            'color: #f59e0b; font-weight: bold;'
          );
          return {
            selectedTool: newToolId,
            selectedSubType: defaultSubType,
            selectedModel: linkedModel,
            previousModel: state.selectedModel // 이전 모델 저장
          };
        }

        // 도구 해제 시 이전 모델로 복원
        if (!newToolId && state.previousModel) {
          console.log(
            `%c[STORE] 이전 모델 복원: ${state.previousModel?.name}`,
            'color: #10b981; font-weight: bold;'
          );
          return {
            selectedTool: newToolId,
            selectedSubType: null,
            selectedModel: state.previousModel,
            previousModel: null
          };
        }

        return { selectedTool: newToolId, selectedSubType: defaultSubType };
      }),

      setSelectedSubType: (subType) => set((state) => {
        console.log(
          `%c[STORE] 서브타입 변경: ${state.selectedSubType?.name || '없음'} → ${subType?.name || '해제'}`,
          'color: #06b6d4; font-weight: bold;'
        );
        return { selectedSubType: subType };
      }),

      setSelectedModel: (model) => set({ selectedModel: model, previousModel: null }),

      setProcessing: (status) => set({ isProcessing: status }),

      setLoadingConversation: (status) => set({ isLoadingConversation: status }),

      resetChat: () => set({
        messages: [],
        currentConversationId: null,
        streamingMessage: null,
        selectedTool: null,
        selectedSubType: null, // 서브타입도 리셋
        selectedModel: DEFAULT_MODEL, // 기본 모델(Claude Sonnet 4.6)로 리셋
        previousModel: null,
        newChatRequested: true, // Signal that new chat was requested
        isProcessing: false, // 🔧 스트리밍 중 이탈 시 스피너 즉시 중지
        isLoadingConversation: false, // 🔧 로딩 상태도 초기화
        // 팟캐스트 퍼널도 리셋
        podcastFunnel: {
          isActive: false,
          step: 1,
          topic: '',
          style: null,
          duration: null,
        },
      }),

      clearNewChatRequest: () => set({ newChatRequested: false }),

      // 팟캐스트 퍼널 액션
      startPodcastFunnel: () => set({
        podcastFunnel: {
          isActive: true,
          step: 1,
          topic: '',
          style: null,
          duration: null,
        }
      }),

      setPodcastTopic: (topic) => set((state) => ({
        podcastFunnel: { ...state.podcastFunnel, topic, step: 2 }
      })),

      setPodcastStyle: (style) => set((state) => ({
        podcastFunnel: { ...state.podcastFunnel, style, step: 3 }
      })),

      setPodcastDuration: (duration) => set((state) => ({
        podcastFunnel: { ...state.podcastFunnel, duration, step: 4 }
      })),

      goBackPodcastStep: () => set((state) => ({
        podcastFunnel: { ...state.podcastFunnel, step: Math.max(1, state.podcastFunnel.step - 1) }
      })),

      resetPodcastFunnel: () => set({
        podcastFunnel: {
          isActive: false,
          step: 1,
          topic: '',
          style: null,
          duration: null,
        }
      }),

      // Computed / Getters
      getCurrentMessages: () => get().messages,

      getLastAssistantMessage: () => {
        const messages = get().messages;
        return messages.filter(m => m.role === 'assistant').pop();
      }
    }),
    {
      name: 'labs-chat-store',
      version: 2,
      migrate: (persistedState, version) => {
        // Force reset selectedModel on version change so DEFAULT_MODEL is used
        return { ...persistedState, selectedModel: undefined };
      },
      partialize: (state) => ({
        // Only persist these fields
        currentConversationId: state.currentConversationId,
        // hidden 모델(나노바나나)은 저장하지 않음
        selectedModel: state.selectedModel?.hidden ? DEFAULT_MODEL : state.selectedModel
      }),
      onRehydrateStorage: () => (state) => {
        // 복원 후 hidden 모델이면 기본 모델로 변경
        if (state?.selectedModel?.hidden) {
          console.log('%c[STORE] hidden 모델 감지, 기본 모델로 복원', 'color: #ef4444;');
          state.selectedModel = DEFAULT_MODEL;
        }
      }
    }
  )
);
